import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAppContext } from 'src/contexts/AppContext';
import { Content } from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { PosCurrencyField } from 'src/core/POS/PosCurrencyField';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { Stack, Switch } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import { PRICING_FLOOR_CEIL_ADJUSTMENT_TYPE_TO_CID } from 'src/utils/constants/contentIdMaps';
import { posChangedField, posField } from 'src/utils/posFieldUtils';
import { getMarketplaceSettings } from 'src/utils/userUtils';
import {
  ListingDetailsAutoPricingSectionUpdatesV2,
  Marketplace,
} from 'src/WebApiController';

import { AutoPricingSettingsInput } from '../../BulkEditAutoPricingSettingsDialog.types';
import {
  getFloorCeilingAllInDisplayValue,
  hasFloorCeilingChanged,
  toAllInFloorCeilingFormValue,
} from '../../BulkEditAutoPricingSettingsDialog.utils';
import { useAutoPricingSettings } from '../../useBulkAutoPricingSettings';
import { PricingSetting } from '../PricingSetting';
import { SettingsSubheader } from '../PricingSubheader';
import { FloorAdjustmentInput } from './FloorAdjustmentInput';
import * as styles from './PricingProtection.css';

type PricingProtectionProps = {
  origin: Pick<
    AutoPricingSettingsInput,
    'netProceedsFloor' | 'netProceedsCeiling'
  >;
};

export const PricingProtection: React.FC<PricingProtectionProps> = ({
  origin,
}) => {
  const { loginContext } = useAppContext();
  const { getUiCurrency } = useLocalizationContext();
  const activeAccount = loginContext?.user?.activeAccount;
  const isOfficeEnabled = activeAccount?.isOfficeEnabled === true;
  const stubHubMkpSettings = getMarketplaceSettings(
    activeAccount,
    Marketplace.StubHub
  );

  const uiCurrency = getUiCurrency(
    loginContext?.user?.activeAccount?.currencyCode
  );

  const { setValue, watch, getValues } =
    useFormContext<ListingDetailsAutoPricingSectionUpdatesV2>();

  const isFloorCeilingAllInPrice = watch('isFloorCeilingAllInPrice') === true;

  const [isAbsolute, setIsAbsolute] = useState<boolean>(false);

  const {
    netProceedsCeiling,
    netProceedsFloor,
    netProceedsFloorAdjustmentAmount,
    netProceedsFloorAdjustmentRelative,
    netProceedsFloorAdjustmentType,
    onUpdateNetProceedsFloorAdjustment,
  } = useAutoPricingSettings();

  const onPriceChange = useCallback(
    (
      field: 'netProceedsFloor' | 'netProceedsCeiling',
      otherField: 'netProceedsFloor' | 'netProceedsCeiling',
      isValid: boolean,
      v: number
    ) => {
      setValue(
        field,
        isValid
          ? posChangedField(
              toAllInFloorCeilingFormValue(
                v,
                isOfficeEnabled,
                stubHubMkpSettings?.sellerFee
              )
            )
          : null
      );
      if (isOfficeEnabled) {
        const other = getValues(otherField);
        const hasChangedValue =
          isValid || (other?.hasChanged === true && other.value != null);
        setValue('isFloorCeilingAllInPrice', hasChangedValue ? true : null);
      }
    },
    [setValue, getValues, isOfficeEnabled, stubHubMkpSettings?.sellerFee]
  );

  const onPriceFloorChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const v = parseFloat(e.target.value);
      onPriceChange(
        'netProceedsFloor',
        'netProceedsCeiling',
        v >= 0 && v < Number.MAX_VALUE,
        v
      );
    },
    [onPriceChange]
  );

  const onPriceCeilingChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const v = parseFloat(e.target.value);
      onPriceChange(
        'netProceedsCeiling',
        'netProceedsFloor',
        v >= 0 && v <= Number.MAX_VALUE,
        v
      );
    },
    [onPriceChange]
  );

  const netProceedsFloorWebsite = useMemo(
    () =>
      getFloorCeilingAllInDisplayValue(
        netProceedsFloor,
        isFloorCeilingAllInPrice,
        stubHubMkpSettings?.sellerFee
      ) ?? '',
    [netProceedsFloor, isFloorCeilingAllInPrice, stubHubMkpSettings?.sellerFee]
  );

  const netProceedsCeilingWebsite = useMemo(
    () =>
      getFloorCeilingAllInDisplayValue(
        netProceedsCeiling,
        isFloorCeilingAllInPrice,
        stubHubMkpSettings?.sellerFee
      ) ?? '',
    [
      netProceedsCeiling,
      isFloorCeilingAllInPrice,
      stubHubMkpSettings?.sellerFee,
    ]
  );

  const floorOrCeilingHasChange = useMemo(
    () =>
      // An all-in value can equal the old proceeds value, so check the all-in flag before comparing values
      hasFloorCeilingChanged(
        netProceedsCeiling,
        origin.netProceedsCeiling,
        isFloorCeilingAllInPrice
      ) ||
      hasFloorCeilingChanged(
        netProceedsFloor,
        origin.netProceedsFloor,
        isFloorCeilingAllInPrice
      ) ||
      origin.netProceedsCeiling?.value !== netProceedsCeiling?.value ||
      origin.netProceedsFloor?.value !== netProceedsFloor?.value,
    [
      netProceedsCeiling,
      netProceedsFloor,
      isFloorCeilingAllInPrice,
      origin.netProceedsCeiling,
      origin.netProceedsFloor,
    ]
  );

  const onResetPricingProtection = useCallback(() => {
    setValue('netProceedsCeiling', posField(origin.netProceedsCeiling?.value));
    setValue('netProceedsFloor', posField(origin.netProceedsFloor?.value));
    setValue('isFloorCeilingAllInPrice', null);
  }, [
    origin.netProceedsCeiling?.value,
    origin.netProceedsFloor?.value,
    setValue,
  ]);

  const onRelativeSwitch = useCallback(
    (checked: boolean) => {
      setIsAbsolute(checked);
      onResetPricingProtection();
    },
    [onResetPricingProtection]
  );

  return (
    <>
      <SettingsSubheader title={ContentId.PricingProtection} />
      {!isAbsolute ? (
        <PricingSetting
          header={<Content id={ContentId.AdjustFloor} />}
          detail={
            <Stack gap="l" alignItems="center">
              <FloorAdjustmentInput
                amount={netProceedsFloorAdjustmentAmount}
                isRelative={netProceedsFloorAdjustmentRelative}
                onChange={(amount, isRelative) => {
                  onUpdateNetProceedsFloorAdjustment({
                    adjustmentAmount: amount,
                    isRelative,
                  });
                }}
              />

              <div className={styles.inlinePricingInputText}>
                <Content id={ContentId.RelativeTo} />
              </div>

              <PosEnumSelect
                style={{ width: '100%', height: '40px' }}
                searchable={true}
                value={netProceedsFloorAdjustmentType}
                valueOptionsContent={PRICING_FLOOR_CEIL_ADJUSTMENT_TYPE_TO_CID}
                onChange={(newValue) => {
                  if (
                    newValue != null &&
                    newValue != netProceedsFloorAdjustmentType
                  ) {
                    onUpdateNetProceedsFloorAdjustment({
                      adjustmentType: newValue,
                    });
                  }
                }}
              />
            </Stack>
          }
          hasChange={
            netProceedsFloorAdjustmentAmount != null &&
            netProceedsFloorAdjustmentRelative != null &&
            !!netProceedsFloorAdjustmentType
          }
          onReset={() => onUpdateNetProceedsFloorAdjustment({})}
        />
      ) : (
        <PricingSetting
          header={<Content id={ContentId.DontPriceBelow} />}
          detail={
            <Stack gap="l" alignItems="center">
              <PosFormField style={{ width: 'fit-content' }}>
                <PosCurrencyField
                  placeholder={undefined}
                  uiCurrency={uiCurrency}
                  value={netProceedsFloorWebsite}
                  onChange={onPriceFloorChange}
                />
              </PosFormField>
              <div className={styles.inlinePricingInputText}>
                <Content id={ContentId.OrAbove} />
              </div>
              <PosFormField style={{ width: 'fit-content' }}>
                <PosCurrencyField
                  placeholder={undefined}
                  uiCurrency={uiCurrency}
                  value={netProceedsCeilingWebsite}
                  onChange={onPriceCeilingChange}
                />
              </PosFormField>
            </Stack>
          }
          hasChange={floorOrCeilingHasChange}
          hasConflict={
            origin.netProceedsFloor?.hasConflict ||
            origin.netProceedsCeiling?.hasConflict
          }
          onReset={onResetPricingProtection}
        />
      )}
      <PricingSetting
        header={<Content id={ContentId.UseAbsoluteFloors} />}
        detail={
          <Stack width="fit">
            <Switch
              checked={isAbsolute}
              onCheckedChange={onRelativeSwitch}
              onChange={(event) => event.stopPropagation()}
            />
          </Stack>
        }
      />
    </>
  );
};
